import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client'; // Import Socket.IO client

// Define common styles within the component's JavaScript for React styling best practices
// These styles are specific to the wrapper and its loading overlay.
const wrapperStyles = `
    /* Overall app container styling */
    #python-app-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        max-width: 1000px; /* Adjust as needed */
        width: 100%;
        margin: 20px auto;
        background-color: #1a202c; /* Dark background */
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 255, 255, 0.3); /* Cyan glow */
        color: #e2e8f0; /* Light text */
        font-family: 'Inter', sans-serif;
        position: relative; /* Needed for absolute positioning of loading overlay */
        min-height: 700px; /* Ensure enough height for loader to be visible */
        box-sizing: border-box; /* Include padding/border in element's total width/height */
        /* Removed overflow: hidden; here to allow content to scroll if needed within the modal */
    }

    /* Custom styles for Elementor/WordPress compatibility, applying directly to the container */
    #python-app-container {
        padding: 20px !important;
        border-radius: 5px !important; /* Overrides 12px if needed by WordPress */
    }
    body, html { /* Global background color - this should ideally be in your main React app's CSS */
        background-color: #111111 !important;
    }
    .elementor-location-footer { /* Hides specific Elementor footer */
        display: none;
    }

    /* --- Loading Overlay Styles --- */
    #loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(26, 32, 44, 0.95); /* Semi-transparent dark background */
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: #e2e8f0;
        font-size: 1.5rem;
        font-weight: bold;
        z-index: 1000; /* Ensure it's on top */
        border-radius: 12px; /* Match container border-radius */
        transition: opacity 0.5s ease-in-out; /* Smooth transition for hiding */
    }

    #loading-overlay.hidden {
        opacity: 0;
        pointer-events: none; /* Allow clicks through when hidden */
        /* Optionally set display: none after transition for accessibility, but opacity is often sufficient */
    }
    #loading-overlay p
    {
        padding: 0% clamp(20px, 20%, 20vw);
    }
    .spinner {
        border: 4px solid rgba(0, 255, 255, 0.3);
        border-top: 4px solid #00ffff;
        border-radius: 50%;
        width: 50px;
        height: 50px;
        animation: spin 1s linear infinite;
        margin-bottom: 20px;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    #connection-status {
        margin-top: 10px;
        font-size: 1.2rem;
        color: #a0aec0;
        text-align: center; /* Center the multi-line text */
    }
    
    @media (max-width: 767px)
    {
        #loading-overlay p
        {
            padding: 0% 20px;
        }
    }
`;

function PythonAppWrapper({ backendUrl, children }) {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionMessage, setConnectionMessage] = useState('Connecting to server...');
    const [reconnectAttempt, setReconnectAttempt] = useState(0);

    useEffect(() => {
        // Only initialize socket if backendUrl is provided
        if (!backendUrl) {
            console.error("PythonAppWrapper: backendUrl prop is missing!");
            setConnectionMessage("Error: App URL missing. Please check configuration.");
            return;
        }

        console.log(`PythonAppWrapper: Attempting to connect to ${backendUrl}`);
        // Initialize Socket.IO client
        const newSocket = io(backendUrl, {
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            transports: ['websocket', 'polling'], // Ensure proper transport fallback
        });

        newSocket.on('connect', () => {
            console.log('PythonAppWrapper: Connected to server via WebSocket');
            setIsConnected(true);
            setConnectionMessage('');
            setReconnectAttempt(0);
        });

        newSocket.on('disconnect', (reason) => {
            console.log(`PythonAppWrapper: Disconnected from server. Reason: ${reason}`);
            setIsConnected(false);
            setConnectionMessage('Disconnected. Attempting to reconnect...');
        });

        newSocket.on('reconnecting', (attemptNumber) => {
            setReconnectAttempt(attemptNumber);
            console.log(`PythonAppWrapper: Attempting to reconnect: ${attemptNumber}`);
            setConnectionMessage(`Connecting to server... Attempt ${attemptNumber}`);
        });

        newSocket.on('reconnect_error', (error) => {
            console.error('PythonAppWrapper: Reconnection error:', error);
            setConnectionMessage(`Reconnection error: ${error.message}.`);
        });

        newSocket.on('reconnect_failed', () => {
            console.error('PythonAppWrapper: Reconnection failed completely.');
            setConnectionMessage('Failed to connect to server after multiple attempts. Please try again later.');
        });

        newSocket.on('error', (error) => {
            console.error('PythonAppWrapper: Socket error:', error);
            setConnectionMessage(`Error: ${error.message}`);
        });

        setSocket(newSocket); // Store the socket instance in state

        // Cleanup function for useEffect
        return () => {
            console.log('PythonAppWrapper: Disconnecting socket on unmount.');
            if (newSocket.connected) {
                newSocket.disconnect();
            }
            newSocket.offAny(); // Remove all listeners
        };
    }, [backendUrl]); // Re-run effect if backendUrl changes

    return (
        <div id="python-app-container">
            {/* Inject global styles */}
            <style>{wrapperStyles}</style>

            {/* Loading Overlay */}
            {!isConnected && (
                <div id="loading-overlay">
                    <div className="spinner"></div>
                    <p>{connectionMessage}</p>
                    {/* Added custom message */}
                    {/*<p style={{ fontWeight: 300, fontSize: '1em', lineHeight: '1.4em', marginTop: '10px' }}>
                        The application relies on an external server to run the python code.<br/>
                        It might take a few seconds for the server to launch the app...
                    </p>*/}
                    <p style={{ fontWeight: 300, fontSize: '1em', lineHeight: '1.4em', marginTop: '10px' }}>
                        This app runs on a free server and may experience initial 'cold starts' after inactivity, taking a few seconds to load.
                    </p>
                </div>
            )}

            {/* Render children only when connected */}
            {isConnected && socket && (
                // Pass the socket instance down to the child component
                React.Children.map(children, child =>
                    React.cloneElement(child, { socket: socket })
                )
            )}
        </div>
    );
}

export default PythonAppWrapper;
